import React, { createContext, useContext, useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { colorTokens, spacingTokens, borderRadiusTokens, typographyTokens, shadowTokens } from "../config/designTokens";

// Toast types
export type ToastType = "success" | "error" | "warning" | "info";

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
`;

// Styled components
const ToastContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacingTokens.sm};
  max-width: 400px;
  width: calc(100% - ${spacingTokens.xxl});
  pointer-events: none;

  @media (max-width: 480px) {
    width: auto;
    max-width: 90vw;
  }
`;

const ToastItem = styled.div<{ $type: ToastType; $isExiting?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${spacingTokens.md};
  padding: ${spacingTokens.md} ${spacingTokens.lg};
  background-color: ${colorTokens.light.background.paper};
  border: 1px solid ${colorTokens.light.border.default};
  border-left: 4px solid ${props => {
    switch (props.$type) {
      case "success": return colorTokens.light.brand.primary;
      case "error": return colorTokens.light.neutral.grey[700];
      case "warning": return colorTokens.light.neutral.grey[500];
      case "info": return colorTokens.light.neutral.grey[400];
      default: return colorTokens.light.border.default;
    }
  }};
  border-radius: ${borderRadiusTokens.none};
  box-shadow: ${shadowTokens.md};
  animation: ${props => props.$isExiting ? fadeOut : fadeIn} 0.3s ease forwards;
  pointer-events: auto; // Re-enable clicks for the toast itself
`;

const ToastIcon = styled.span`
  font-size: ${typographyTokens.fontSize.lg};
  flex-shrink: 0;
`;

const ToastContent = styled.div`
  flex: 1;
`;

const ToastMessage = styled.p`
  margin: 0;
  font-family: ${typographyTokens.fontFamily.primary};
  font-size: ${typographyTokens.fontSize.body};
  color: ${colorTokens.light.text.primary};
  line-height: 1.4;
`;

const ToastCloseButton = styled.button`
  background: none;
  border: none;
  padding: ${spacingTokens.xs};
  cursor: pointer;
  color: ${colorTokens.light.text.secondary};
  font-size: ${typographyTokens.fontSize.body};
  flex-shrink: 0;
  transition: color 0.2s ease;

  &:hover {
    color: ${colorTokens.light.text.primary};
  }
`;

// Toast icons
const getToastIcon = (type: ToastType): string => {
  switch (type) {
    case "success": return "✓";
    case "error": return "✕";
    case "warning": return "⚠";
    case "info": return "ℹ";
    default: return "ℹ";
  }
};

// Provider component
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());

  const removeToast = useCallback((id: string) => {
    // Start exit animation
    setExitingIds(prev => new Set(prev).add(id));

    // Remove after animation
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
      setExitingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 300);
  }, []);

  const showToast = useCallback((message: string, type: ToastType = "info", duration: number = 5000) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    setToasts(prev => [...prev, { id, message, type, duration }]);

    // Auto-dismiss
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  }, [removeToast]);

  const showSuccess = useCallback((message: string, duration?: number) => {
    showToast(message, "success", duration);
  }, [showToast]);

  const showError = useCallback((message: string, duration?: number) => {
    showToast(message, "error", duration ?? 7000); // Errors show longer
  }, [showToast]);

  const showWarning = useCallback((message: string, duration?: number) => {
    showToast(message, "warning", duration);
  }, [showToast]);

  const showInfo = useCallback((message: string, duration?: number) => {
    showToast(message, "info", duration);
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, showWarning, showInfo, removeToast }}>
      {children}
      <ToastContainer>
        {toasts.map(toast => (
          <ToastItem
            key={toast.id}
            $type={toast.type}
            $isExiting={exitingIds.has(toast.id)}
          >
            <ToastIcon>{getToastIcon(toast.type)}</ToastIcon>
            <ToastContent>
              <ToastMessage>{toast.message}</ToastMessage>
            </ToastContent>
            <ToastCloseButton onClick={() => removeToast(toast.id)}>
              ✕
            </ToastCloseButton>
          </ToastItem>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
};

// Hook to use toast
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
